import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useAgents, refreshAgents, useContactRefresh } from '../hooks/useApiHooks';
import { useLanguage, getReviewAgentsTranslation } from '../hooks/useLanguage';
import { fetchTemplates } from '../store/reducers';
import { fetchAdditionalContextPresets } from '../store/reducers';
import { SelectionModal } from './common/SelectionModal';
import { PreferenceField } from './common/PreferenceField';

export const PreferencesModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  
  // Fetch data hooks
  useAgents();
  
  // Get data from Redux state
  const agents = useSelector((state) => state.agents || []);
  const templates = useSelector((state) => {
    const templates = state.templates;
    return Array.isArray(templates) ? templates : [];
  });
  const additionalContextPresets = useSelector((state) => {
    const presets = state.additionalContextPresets;
    return Array.isArray(presets) ? presets : [];
  });
  
  // Preferred values state
  const [preferredAgent, setPreferredAgent] = useState(null);
  const [preferredTemplate, setPreferredTemplate] = useState(null);
  const [preferredAdditionalContext, setPreferredAdditionalContext] = useState(null);
  
  // Modal states
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isAdditionalContextModalOpen, setIsAdditionalContextModalOpen] = useState(false);
  
  // Search and sort states for each modal
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [agentSortBy, setAgentSortBy] = useState('name-asc');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateSortBy, setTemplateSortBy] = useState('name-asc');
  const [additionalContextSearchQuery, setAdditionalContextSearchQuery] = useState('');
  const [additionalContextSortBy, setAdditionalContextSortBy] = useState('name-asc');
  const { refresh: handleRefreshAgents, isRefreshing: isRefreshingAgents } = useContactRefresh(refreshAgents);
  
  // Fetch templates and additional context presets when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchTemplates());
      dispatch(fetchAdditionalContextPresets());
    }
  }, [isOpen, dispatch]);

  // Load preferred values from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      // Load preferred agent
      const storedAgent = localStorage.getItem('preferredAgent');
      if (storedAgent) {
        try {
          setPreferredAgent(JSON.parse(storedAgent));
        } catch (e) {
          console.error('Error parsing preferred agent:', e);
        }
      }
      
      // Load preferred template
      const storedTemplate = localStorage.getItem('preferredTemplate');
      if (storedTemplate) {
        try {
          setPreferredTemplate(JSON.parse(storedTemplate));
        } catch (e) {
          console.error('Error parsing preferred template:', e);
        }
      }
      
      // Load preferred additional context
      const storedAdditionalContext = localStorage.getItem('preferredAdditionalContext');
      if (storedAdditionalContext) {
        try {
          setPreferredAdditionalContext(JSON.parse(storedAdditionalContext));
        } catch (e) {
          console.error('Error parsing preferred additional context:', e);
        }
      }
    }
  }, [isOpen]);

  // Handler functions for each preference type
  const handleAgentSelect = (agent) => {
    setPreferredAgent(agent);
    if (agent) {
      localStorage.setItem('preferredAgent', JSON.stringify(agent));
    } else {
      localStorage.removeItem('preferredAgent');
    }
    setIsAgentModalOpen(false);
    setAgentSearchQuery('');
  };
  
  const handleTemplateSelect = (template) => {
    setPreferredTemplate(template);
    if (template) {
      localStorage.setItem('preferredTemplate', JSON.stringify(template));
    } else {
      localStorage.removeItem('preferredTemplate');
    }
    setIsTemplateModalOpen(false);
    setTemplateSearchQuery('');
  };
  
  const handleAdditionalContextSelect = (preset) => {
    setPreferredAdditionalContext(preset);
    if (preset) {
      localStorage.setItem('preferredAdditionalContext', JSON.stringify(preset));
      // Dispatch custom event to notify other components (for same-tab updates)
      window.dispatchEvent(new CustomEvent('preferredAdditionalContextChanged'));
    } else {
      localStorage.removeItem('preferredAdditionalContext');
      window.dispatchEvent(new CustomEvent('preferredAdditionalContextChanged'));
    }
    setIsAdditionalContextModalOpen(false);
    setAdditionalContextSearchQuery('');
  };

  // Filter functions for each type
  const filterAgents = (items, query) => {
    return items.filter(agent => {
      if (!agent || typeof agent !== 'object') return false;
      const searchInField = (field) => {
        if (field == null) return false;
        if (typeof field === 'string' && field.trim() === '') return false;
        if (typeof field === 'number') return String(field).toLowerCase().includes(query);
        return String(field).toLowerCase().trim().includes(query);
      };
      return searchInField(agent.nombre) ||
             searchInField(agent.identificacion_fiscal) ||
             searchInField(agent.nif) ||
             searchInField(agent.eori) ||
             searchInField(agent.direccion) ||
             searchInField(agent.localidad) ||
             searchInField(agent.pais);
    });
  };
  
  const filterTemplates = (items, query) => {
    return items.filter(template => {
      if (!template || typeof template !== 'object') return false;
      const searchLower = query.toLowerCase();
      return (template.title && template.title.toLowerCase().includes(searchLower)) ||
             (template.id && String(template.id).toLowerCase().includes(searchLower));
    });
  };
  
  const filterAdditionalContext = (items, query) => {
    return items.filter(preset => {
      if (!preset || typeof preset !== 'object') return false;
      const searchLower = query.toLowerCase();
      return (preset.name && preset.name.toLowerCase().includes(searchLower)) ||
             (preset.content && preset.content.toLowerCase().includes(searchLower));
    });
  };
  
  // Sort options for each type
  const agentSortOptions = [
    { value: 'name-asc', label: getReviewAgentsTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getReviewAgentsTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getReviewAgentsTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getReviewAgentsTranslation('oldestFirst', isSpanish) }
  ];
  
  const templateSortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
  ];
  
  const additionalContextSortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Main Preferences Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[50] animate-fadeIn">
        <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4 animate-modalEnter">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {isSpanish ? 'Preferencias' : 'Preferences'}
            </h2>
            <button 
              onClick={onClose} 
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4 mt-8">
            {/* Preferred Agent Field */}
            <PreferenceField
              label={isSpanish ? 'Agente Preferido' : 'Preferred Agent'}
              value={preferredAgent}
              displayValue={preferredAgent?.nombre}
              placeholder={getReviewAgentsTranslation('selectAgent', isSpanish)}
              onSelect={() => setIsAgentModalOpen(true)}
              onClear={() => handleAgentSelect(null)}
              isSpanish={isSpanish}
            />
            
            {/* Preferred Template Field */}
            <PreferenceField
              label={isSpanish ? 'Plantilla Preferida' : 'Preferred Template'}
              value={preferredTemplate}
              displayValue={preferredTemplate?.title}
              placeholder={isSpanish ? 'Seleccionar plantilla' : 'Select template'}
              onSelect={() => setIsTemplateModalOpen(true)}
              onClear={() => handleTemplateSelect(null)}
              isSpanish={isSpanish}
            />
            
            {/* Preferred Additional Context Field */}
            <PreferenceField
              label={isSpanish ? 'Contexto Adicional Preferido' : 'Preferred Additional Context'}
              value={preferredAdditionalContext}
              displayValue={preferredAdditionalContext?.name || (preferredAdditionalContext?.content ? (preferredAdditionalContext.content.substring(0, 30) + '...') : null)}
              placeholder={isSpanish ? 'Seleccionar contexto adicional' : 'Select additional context'}
              onSelect={() => setIsAdditionalContextModalOpen(true)}
              onClear={() => handleAdditionalContextSelect(null)}
              isSpanish={isSpanish}
            />
          </div>
        </div>
      </div>

      {/* Agent Selection Modal */}
      <SelectionModal
        isOpen={isAgentModalOpen}
        onClose={() => {
          setIsAgentModalOpen(false);
          setAgentSearchQuery('');
        }}
        title={getReviewAgentsTranslation('selectAgent', isSpanish)}
        items={agents}
        searchQuery={agentSearchQuery}
        setSearchQuery={setAgentSearchQuery}
        sortBy={agentSortBy}
        setSortBy={setAgentSortBy}
        sortOptions={agentSortOptions}
        onSelect={handleAgentSelect}
        searchPlaceholder={getReviewAgentsTranslation('searchAgents', isSpanish)}
        emptyMessage={getReviewAgentsTranslation('noAgentsFound', isSpanish)}
        noItemsMessage={getReviewAgentsTranslation('noAgentsAvailable', isSpanish)}
        onRefresh={handleRefreshAgents}
        isRefreshing={isRefreshingAgents}
        refreshTitle={getReviewAgentsTranslation('refreshList', isSpanish)}
        getItemKey={(agent, index) => `pref-agent-${agent.id || index}-${index}`}
        filterItems={filterAgents}
        renderItem={(agent) => (
          <div className="flex justify-between items-start gap-2">
            <div>
              <p className="font-medium">{agent.nombre}</p>
              <p className="text-sm text-gray-500">{agent.pais}</p>
            </div>
            {agent.nif && (
              <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                {agent.nif}
              </span>
            )}
          </div>
        )}
      />
      
      {/* Template Selection Modal */}
      <SelectionModal
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setTemplateSearchQuery('');
        }}
        title={isSpanish ? 'Seleccionar Plantilla' : 'Select Template'}
        items={templates}
        searchQuery={templateSearchQuery}
        setSearchQuery={setTemplateSearchQuery}
        sortBy={templateSortBy}
        setSortBy={setTemplateSortBy}
        sortOptions={templateSortOptions}
        onSelect={handleTemplateSelect}
        searchPlaceholder={isSpanish ? 'Buscar plantillas...' : 'Search templates...'}
        emptyMessage={isSpanish ? 'No se encontraron plantillas' : 'No templates found'}
        noItemsMessage={isSpanish ? 'No hay plantillas disponibles' : 'No templates available'}
        getItemKey={(template, index) => `pref-template-${template.id || index}-${index}`}
        filterItems={filterTemplates}
        renderItem={(template) => (
          <div className="flex justify-between items-start gap-2">
            <div className="flex items-center gap-2">
              {template.color && (
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: template.color }}
                />
              )}
              <p className="font-medium">{template.title || template.id}</p>
            </div>
          </div>
        )}
      />
      
      {/* Additional Context Selection Modal */}
      <SelectionModal
        isOpen={isAdditionalContextModalOpen}
        onClose={() => {
          setIsAdditionalContextModalOpen(false);
          setAdditionalContextSearchQuery('');
        }}
        title={isSpanish ? 'Seleccionar Contexto Adicional' : 'Select Additional Context'}
        items={additionalContextPresets}
        searchQuery={additionalContextSearchQuery}
        setSearchQuery={setAdditionalContextSearchQuery}
        sortBy={additionalContextSortBy}
        setSortBy={setAdditionalContextSortBy}
        sortOptions={additionalContextSortOptions}
        onSelect={handleAdditionalContextSelect}
        searchPlaceholder={isSpanish ? 'Buscar contexto adicional...' : 'Search additional context...'}
        emptyMessage={isSpanish ? 'No se encontró contexto adicional' : 'No additional context found'}
        noItemsMessage={isSpanish ? 'No hay contexto adicional disponible' : 'No additional context available'}
        getItemKey={(preset, index) => `pref-additional-context-${preset.id || index}-${index}`}
        filterItems={filterAdditionalContext}
        renderItem={(preset) => (
          <div className="flex flex-col gap-1">
            {preset.name && (
              <p className="font-medium">{preset.name}</p>
            )}
            {preset.content && (
              <p className="text-sm text-gray-500 line-clamp-2">
                {preset.content.length > 100 
                  ? preset.content.substring(0, 100) + '...' 
                  : preset.content}
              </p>
            )}
          </div>
        )}
      />
    </>
  );
};
